// The org-lens path-rewrite — the load-bearing realization of "BandContext.shell picks the
// org-prefixed path; feature hooks NEVER choose the prefix." The RosterShell activates a lens (its serving
// orgId); while active, EVERY band-direct request `/api/v1/bands/{bandId}/…` is spliced to its org mirror
// `/api/v1/orgs/{orgId}/bands/{bandId}/…` (the OrgLensMirrorConvention already serves both). So the ~170
// existing feature hooks resolve the org form invisibly — the whole surface renders under the org lens without a
// single hook touching the prefix, and money-free-ness is whatever the org-lens capabilities the server stamped.
import type { Middleware } from "@cntrl/api-client";

/** The band-direct path prefix the mirror splices `/orgs/{orgId}` into. */
const BAND_DIRECT_PREFIX = "/api/v1/bands/";

// A single mutable holder bridges React (the RosterShell renders with an orgId) → the client singleton (built
// once at bootstrap). Only ONE roster shell is mounted at a time (roster + artist shells are disjoint route
// trees), so a single holder is correct; the shell sets it on mount and CLEARS it on unmount so a later
// artist-shell band read is never rewritten.
const lens: { orgId: string | undefined } = { orgId: undefined };

/** Activate (orgId) or clear (undefined) the roster org-lens. Set by the RosterShell; cleared on its unmount. */
export function setOrgLens(orgId: string | undefined): void {
  lens.orgId = orgId;
}

/** The active roster org-lens orgId, or undefined when no roster lens is mounted. */
export function getOrgLens(): string | undefined {
  return lens.orgId;
}

/**
 * The band-scoped API PATH for `suffix`, honouring the active lens — the hand-rolled twin of
 * {@link orgLensMiddleware}, for the handful of requests that do NOT go through the generated client and
 * therefore never meet it.
 *
 * That gap is not theoretical: media BYTES are fetched with a bare `fetch` (they resolve to a blob and an
 * object URL, which openapi-fetch has no shape for), so under the roster lens every photo and logo asked
 * the server for `/api/v1/bands/{bandId}/media/…` — a band-direct route an ORG seat cannot resolve — and
 * came back 403. Silently: `useMediaObjectUrl` degrades to `error: true` and the tile renders empty, so a
 * roster lens showed a band's press kit with all of the metadata and none of the pictures.
 *
 * Anything with a body you can hand to the generated client should use the client and get the rewrite for
 * free. The only other raw fetches are the `/debug` hooks, which are fine as-is for a non-obvious reason
 * worth stating: `/debug` is an artist-shell route with no roster equivalent, so no lens is ever mounted
 * over them. If one ever is, they need this.
 */
export function bandScopedPath(bandId: string, suffix: string): string {
  const orgId = lens.orgId;
  return orgId
    ? `/api/v1/orgs/${orgId}/bands/${bandId}/${suffix}`
    : `${BAND_DIRECT_PREFIX}${bandId}/${suffix}`;
}

/**
 * The rewrite middleware. Gated STRICTLY on `schemaPath.startsWith('/api/v1/bands/')` so org-direct
 * (`/orgs/{orgId}/…`), person (`/me/…`), auth, public, and mics routes are never touched. `request.url`
 * carries the full origin+path (openapi-fetch has already templated it), and the band-direct segment is a
 * literal, so the `.replace` is origin-agnostic. `Request.url` is immutable, so a rewrite REBUILDS the Request.
 *
 * **The body must be BUFFERED into that rebuild, and `new Request(url, request)` does not do it.** Passing a
 * Request as the init reads its `body` getter, which is a `ReadableStream` — so the copy is a STREAMING
 * upload rather than the byte buffer openapi-fetch handed us, and browsers refuse those: Chrome requires
 * HTTP/2+ for a streamed body and fails the cleartext dev-proxy request at the network layer
 * (`ERR_ALPN_NEGOTIATION_FAILED`, surfaced as a bare *"Failed to fetch"*), Safari throws
 * `NotSupportedError: ReadableStream uploading is not supported`. A GET has no body, so the whole roster READ
 * surface was unaffected and only WRITES failed — every mutation under the lens, not one feature's. Found via
 * an asset upload (2026-09-01), whose very first call is the `media/upload-url` mint.
 */
export const orgLensMiddleware: Middleware = {
  async onRequest({ request, schemaPath }) {
    const orgId = lens.orgId;
    if (!orgId || !schemaPath.startsWith(BAND_DIRECT_PREFIX)) return request;

    const url = request.url.replace(BAND_DIRECT_PREFIX, `/api/v1/orgs/${orgId}/bands/`);
    // Read the bytes off a CLONE: the original stays unconsumed, so a caller (or openapi-fetch's own
    // error path) that reads it afterwards still can.
    const body = request.method === "GET" || request.method === "HEAD"
      ? undefined
      : await request.clone().arrayBuffer();
    return new Request(url, {
      method: request.method,
      headers: request.headers,
      ...(body && body.byteLength > 0 ? { body } : {}),
      credentials: request.credentials,
      mode: request.mode,
      cache: request.cache,
      redirect: request.redirect,
      referrer: request.referrer,
      referrerPolicy: request.referrerPolicy,
      integrity: request.integrity,
      keepalive: request.keepalive,
      signal: request.signal,
    });
  },
};
